import { useEffect, useRef, useState } from "react";
import AdminLayout from "../AdminLayout";
import SiteTextsPanel from "../SiteTextsPanel";
import { API_BASE } from "@/config/api";
import { uploadMedia } from "../uploadMedia";
import { normalizeMediaUrl } from "@/lib/mediaUrl";
import type { Bodega, ImagenGaleria } from "@/types/content";

// m2 y valor_uf se editan como texto para que borrar y escribir no deje un 0 pegado; se convierten al guardar.
type Draft = Omit<Bodega, "orden"> & { caracteristicasText: string; m2Text: string; valorUfText: string };

function parseNumber(text: string): number {
  return Number(text.trim().replace(",", "."));
}

function toDraft(b?: Partial<Bodega>): Draft {
  return {
    id: b?.id ?? "",
    nombre: b?.nombre ?? "",
    tipo: b?.tipo ?? "mini",
    m2: b?.m2 ?? 0,
    valor_uf: b?.valor_uf ?? 0,
    oficina: b?.oficina ?? false,
    trifasica: b?.trifasica ?? false,
    disponible: b?.disponible ?? true,
    destacada: b?.destacada ?? false,
    caracteristicas: b?.caracteristicas ?? [],
    imagen_url: b?.imagen_url ?? "",
    imagen_alt: b?.imagen_alt ?? "",
    imagenes: b?.imagenes ?? [],
    caracteristicasText: (b?.caracteristicas ?? []).join(", "),
    m2Text: b?.m2 ? String(b.m2) : "",
    valorUfText: b?.valor_uf ? String(b.valor_uf) : "",
  };
}

export default function AdminBodegas() {
  const [items, setItems] = useState<Bodega[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [error, setError] = useState("");
  const mainFileRef = useRef<HTMLInputElement>(null);
  const galleryFileRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/bootstrap`);
    const json = await res.json();
    setItems(json.bodegas);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleMainImage(file: File) {
    if (!editing) return;
    setUploadingMain(true);
    setError("");
    try {
      const { url } = await uploadMedia(file, `Foto principal de ${editing.nombre || "bodega"}`);
      setEditing({ ...editing, imagen_url: url });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo subir la imagen");
    } finally {
      setUploadingMain(false);
    }
  }

  async function handleGalleryImage(file: File) {
    if (!editing) return;
    setUploadingGallery(true);
    setError("");
    try {
      const { url } = await uploadMedia(file, `Foto de ${editing.nombre || "bodega"}`);
      setEditing({ ...editing, imagenes: [...editing.imagenes, { url, alt: "" }] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo subir la imagen");
    } finally {
      setUploadingGallery(false);
    }
  }

  function updateGalleryAlt(index: number, alt: string) {
    if (!editing) return;
    const imagenes = editing.imagenes.map((img, i) => (i === index ? { ...img, alt } : img));
    setEditing({ ...editing, imagenes });
  }

  function removeGalleryImage(index: number) {
    if (!editing) return;
    setEditing({ ...editing, imagenes: editing.imagenes.filter((_, i) => i !== index) });
  }

  async function handleSave() {
    if (!editing) return;
    const m2 = parseNumber(editing.m2Text);
    const valorUf = parseNumber(editing.valorUfText);
    if (!editing.m2Text.trim() || !Number.isFinite(m2) || m2 <= 0) {
      setError("Ingresa los m² como un número mayor a 0.");
      return;
    }
    if (!editing.valorUfText.trim() || !Number.isFinite(valorUf) || valorUf < 0) {
      setError("Ingresa el valor mensual en UF como un número (por ejemplo 2,5).");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      ...editing,
      m2,
      valor_uf: valorUf,
      caracteristicas: editing.caracteristicasText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    const isNew = !items.some((i) => i.id === editing.id);
    try {
      const res = await fetch(isNew ? `${API_BASE}/bodegas` : `${API_BASE}/bodegas/${editing.id}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || "No se pudo guardar");
        return;
      }
      setEditing(null);
      await load();
    } catch {
      setError("Error de conexión.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("¿Eliminar esta unidad?")) return;
    await fetch(`${API_BASE}/bodegas/${id}`, { method: "DELETE" });
    await load();
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
  const labelCls = "block text-xs font-medium text-foreground-600 mb-1";
  const uploadBtnCls =
    "shrink-0 rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">Bodegas</h1>
        <button
          type="button"
          onClick={() => setEditing(toDraft())}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          + Agregar unidad
        </button>
      </div>

      <div className="mt-6 max-w-2xl">
        <SiteTextsPanel
          title="Encabezado de la sección «Encuentra tu espacio» (inicio)"
          description="El texto que va sobre las tarjetas de bodegas en la página de inicio, y los enlaces de cada tarjeta."
          keys={[
            "home.encuentra.etiqueta",
            "home.encuentra.titulo",
            "home.encuentra.descripcion",
            "home.encuentra.ver_detalle",
            "home.encuentra.cotizar",
            "home.encuentra.ver_catalogo",
          ]}
        />
      </div>

      <h2 className="mt-8 font-heading font-semibold text-lg text-foreground-900">Unidades</h2>
      {loading ? (
        <p className="mt-4 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-3">
                {b.imagen_url && (
                  <img src={normalizeMediaUrl(b.imagen_url)} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />
                )}
                <div>
                  <p className="text-sm font-semibold text-foreground-900">
                    {b.nombre} <span className="text-xs text-foreground-400">({b.tipo})</span>
                  </p>
                  <p className="text-xs text-foreground-500">
                    {b.m2} m² · UF {b.valor_uf} · {b.disponible ? "Disponible" : "Ocupado"}
                    {b.destacada && " · Destacada"} · {b.imagenes?.length ?? 0} fotos en galería
                  </p>
                </div>
              </div>
              <div className="flex gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditing(toDraft(b))}
                  className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(b.id)}
                  className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div className="w-full max-w-lg rounded-lg bg-background-50 p-6 mt-10 mb-10">
            <h2 className="font-heading font-semibold text-lg text-foreground-900">
              {items.some((i) => i.id === editing.id) ? "Editar unidad" : "Nueva unidad"}
            </h2>
            {error && (
              <div className="mt-3 rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>
            )}
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Nombre</label>
                <input
                  className={inputCls}
                  value={editing.nombre}
                  onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select
                    className={inputCls}
                    value={editing.tipo}
                    onChange={(e) => setEditing({ ...editing, tipo: e.target.value as Bodega["tipo"] })}
                  >
                    <option value="mini">Mini bodega</option>
                    <option value="bodega">Bodega</option>
                    <option value="galpon">Galpón</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>m²</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className={inputCls}
                    value={editing.m2Text}
                    onChange={(e) => setEditing({ ...editing, m2Text: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Valor mensual (UF)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  className={inputCls}
                  value={editing.valorUfText}
                  onChange={(e) => setEditing({ ...editing, valorUfText: e.target.value })}
                />
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-foreground-700">
                {(["oficina", "trifasica", "disponible", "destacada"] as const).map((field) => (
                  <label key={field} className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={editing[field]}
                      onChange={(e) => setEditing({ ...editing, [field]: e.target.checked })}
                    />
                    {field}
                  </label>
                ))}
              </div>
              <div>
                <label className={labelCls}>Características (separadas por coma)</label>
                <input
                  className={inputCls}
                  value={editing.caracteristicasText}
                  onChange={(e) => setEditing({ ...editing, caracteristicasText: e.target.value })}
                />
              </div>

              <div className="pt-2 border-t border-background-200">
                <label className={labelCls}>Foto principal (la que se ve en el catálogo)</label>
                {editing.imagen_url && (
                  <img src={normalizeMediaUrl(editing.imagen_url)} alt="" className="mt-1 mb-2 w-full h-32 object-cover rounded-md" />
                )}
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    placeholder="URL de la imagen"
                    value={editing.imagen_url}
                    onChange={(e) => setEditing({ ...editing, imagen_url: e.target.value })}
                  />
                  <input
                    ref={mainFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleMainImage(e.target.files[0])}
                  />
                  <button type="button" className={uploadBtnCls} disabled={uploadingMain} onClick={() => mainFileRef.current?.click()}>
                    {uploadingMain ? "Subiendo..." : "Subir"}
                  </button>
                </div>
                <input
                  className={`${inputCls} mt-2`}
                  placeholder="Texto alternativo de la foto principal"
                  value={editing.imagen_alt}
                  onChange={(e) => setEditing({ ...editing, imagen_alt: e.target.value })}
                />
              </div>

              <div className="pt-2 border-t border-background-200">
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Galería (fotos adicionales para el detalle de la unidad)</label>
                  <input
                    ref={galleryFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleGalleryImage(e.target.files[0])}
                  />
                  <button type="button" className={uploadBtnCls} disabled={uploadingGallery} onClick={() => galleryFileRef.current?.click()}>
                    {uploadingGallery ? "Subiendo..." : "+ Agregar foto"}
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {editing.imagenes.map((img: ImagenGaleria, i) => (
                    <div key={img.url + i} className="flex items-center gap-2">
                      <img src={normalizeMediaUrl(img.url)} alt="" className="w-12 h-12 rounded-md object-cover shrink-0" />
                      <input
                        className={inputCls}
                        placeholder="Texto alternativo"
                        value={img.alt}
                        onChange={(e) => updateGalleryAlt(i, e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i)}
                        className="shrink-0 text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                  {editing.imagenes.length === 0 && <p className="text-xs text-foreground-400">Sin fotos adicionales aún.</p>}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-foreground-600 cursor-pointer">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
